import type { CreativeAdSpendSection } from "@nexus/contracts";
import { ApiError, authFetch } from "@/lib/api-client";
import { abortableSleep } from "@/lib/queries/amber-options";
import { OPTIONS_POLL_INTERVAL_MS, OPTIONS_POLL_TIMEOUT_MS } from "@/lib/query-constants";

export function isCreativeAdSpendSection(value: unknown): value is CreativeAdSpendSection {
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  const window = record.window;
  if (!window || typeof window !== "object") return false;
  const dates = window as Record<string, unknown>;
  return (
    typeof dates.startDate === "string" &&
    typeof dates.endDate === "string" &&
    Array.isArray(record.accounts) &&
    Array.isArray(record.rows)
  );
}

export async function pollCreativeAdSpendStatus(
  statusPath: string,
  signal?: AbortSignal,
): Promise<CreativeAdSpendSection> {
  const startedAt = Date.now();
  for (;;) {
    const { data } = await authFetch<{
      status: "ACCEPTED" | "PROCESSING" | "COMPLETED" | "FAILED";
      data?: unknown;
      error?: string;
      message?: string;
    }>(statusPath, { signal });
    const status = data?.status;
    if (status === "COMPLETED") {
      if (isCreativeAdSpendSection(data?.data)) return data.data;
      throw new ApiError(500, data?.error ?? data?.message ?? "Creative ads request failed", "JOB_FAILED");
    }
    if (status === "FAILED") {
      throw new ApiError(500, data?.error ?? data?.message ?? "Creative ads request failed", "JOB_FAILED");
    }
    if (Date.now() - startedAt > OPTIONS_POLL_TIMEOUT_MS) {
      throw new ApiError(504, "Creative ads request timed out", "POLL_TIMEOUT");
    }
    await abortableSleep(OPTIONS_POLL_INTERVAL_MS, signal);
  }
}

export async function fetchCreativeAdSpendSection(
  url: string,
  statusPrefix: "/overview" | "/drilldown",
  signal?: AbortSignal,
): Promise<CreativeAdSpendSection> {
  const { data, correlationId } = await authFetch<CreativeAdSpendSection>(url, { signal });
  if (isCreativeAdSpendSection(data)) return data;
  if (!correlationId) {
    throw new ApiError(500, "Creative ads request returned no data", "JOB_FAILED");
  }
  return pollCreativeAdSpendStatus(`${statusPrefix}/status/${correlationId}`, signal);
}
